import React, {
  useState,
  useMemo,
  useEffect,
  useCallback,
  useContext,
} from "react";
import { useTranslation } from "react-i18next";
import { IdType, Row, SortingRule } from "react-table";
import { HvGrid } from "@hitachivantara/uikit-react-core";
import {
  Settings,
  Fail,
  Success,
  User,
  Edit,
  Delete,
} from "@hitachivantara/uikit-react-icons";

import {
  HvTableColumnConfig,
  HvCellProps,
  useHvTable,
  useHvSortBy,
  useHvPagination,
  useHvBulkActions,
} from "@hitachivantara/uikit-react-lab";

import SimpleTable, {
  DEFAULT_PAGE,
  textColumn,
} from "components/common/SimpleTable";
import ArchiveDialog from "components/common/ArchiveDialog";
import { NotificationContext } from "lib/context/NotificationContext";
import { useSnackbar } from "lib/provider/SnackbarProvider";
import { archiveUsers, useUsers } from "lib/api";

import { useHistory, useRouteMatch } from "react-router-dom";
import useRowSelection from "@hitachivantara/uikit-react-lab/dist/Table/hooks/useRowSelection";
import useStyles from "./styles";

import { joinGroups } from "../../common/UserTable/utils";
import useMembersFilters from "../../common/UserTable/useMembersFilters";
import MemberSettingsTableControls from "../MemberSettingsTableControls";

export interface MembersTableProps {
  assignee?: UserWithGroup;
  members?: UserWithGroup[];
  initialPageSize?: number;
  searchKey?: string;
}

const MembersSettingsTable: React.FC<MembersTableProps> = ({
  assignee,
  members,
  searchKey,
  initialPageSize = 10,
}) => {
  const { t } = useTranslation("memberships");
  const [tableData, setTableData] = useState<MemberDataRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [isArchiveDialogOpen, setIsArchiveDialogOpen] = useState(false);
  const { setShowBanner, setBannerMessage } = useContext(NotificationContext);
  const history = useHistory();
  const { url } = useRouteMatch();

  const { enqueueSnackbar } = useSnackbar();
  const [selectedIds, setSelectedIds] = useState<
    Record<IdType<MemberDataRow>, boolean>
  >({});
  const [size] = useState(initialPageSize);
  const [globalFilter, filterGroups] = useMembersFilters();
  const classes = useStyles();

  const columns = useMemo(
    (): HvTableColumnConfig<MemberDataRow>[] => [
      textColumn({
        Header: t("members.columns.username") as string,
        accessor: "username",
        style: { width: "30%" },
      }),
      textColumn({
        Header: t("members.columns.email") as string,
        accessor: "email",
        style: { width: "30%" },
      }),
      textColumn({
        Header: t("members.columns.accountType") as string,
        accessor: "is_integration",
        style: {
          width: "15%",
          textOverflow: "ellipsis",
          overflow: "hidden",
          whiteSpace: "nowrap",
        },
        Cell: ({ row }: HvCellProps<UserWithGroup, boolean>) =>
          !row.original.is_integration ? (
            <span>
              <User className={classes.accountImage} />

              <span className={classes.accounText}>
                {t("members.labels.userAccount")}
              </span>
            </span>
          ) : (
            <span>
              <Settings className={classes.accountImage} />
              <span className={classes.accounText}>
                {t("members.labels.serviceAccount")}
              </span>
            </span>
          ),
      }),
      textColumn({
        Header: t("members.columns.enabled") as string,
        accessor: "enabled",
        style: { width: "15%" },
        Cell: ({ row }: HvCellProps<UserWithGroup, boolean>) =>
          row.original.enabled ? (
            <Success color="green" className={classes.enabledImage} />
          ) : (
            <Fail color="red" className={classes.enabledImage} />
          ),
      }),
      textColumn({
        Header: t("members.columns.name") as string,
        accessor: "name",
        style: { width: "15%" },
      }),
      textColumn({
        Header: t("members.columns.surname") as string,
        accessor: "surname",
        style: { width: "15%" },
      }),
      textColumn({
        Header: t("members.columns.group") as string,
        accessor: "group",
        style: { width: "15%" },
        disableGlobalFilter: true,
        filter: filterGroups,
      }),
      {
        id: "actions",
        variant: "actions",
        Cell: ({ row }: HvCellProps<UserWithGroup, boolean>) => {
          return (
            <Edit
              style={{ cursor: "pointer" }}
              onClick={() => history.push(`${url}/edit/${row.original.id}`)}
            />
          );
        },
      },
    ],
    [
      t,
      filterGroups,
      classes.accountImage,
      classes.accounText,
      classes.enabledImage,
      history,
      url,
    ]
  );

  const [sortBy] = useState<SortingRule<MemberDataRow>[]>([
    {
      id: "username",
      desc: false,
    },
  ]);

  const {
    data: userData,

    mutate,
  } = useUsers(
    {
      shouldRetryOnError: false,
    },
    {
      pageNr: -1,
      pageSize: -1,
      includeGroups: "true",
      isExcludeServiceAccount: "false",
    }
  );
  const bulkActions = useMemo(
    () => [
      {
        id: "archive",
        label: t("members.labels.bulkArchiveCategoryButton"),
        icon: <Delete />,
        onClick: () => setIsArchiveDialogOpen(true),
      },
    ],
    [t]
  );

  const onArchiveClose = useCallback(
    (result, message, ids) => {
      setIsArchiveDialogOpen(false);

      if (ids) {
        const newSelectedIds = {
          ...selectedIds,
        };

        ids.forEach((id: string) => {
          delete newSelectedIds[id];
        });
        setSelectedIds(newSelectedIds);
      }

      if (result && ids) {
        enqueueSnackbar(message, {
          variant: "success",
        });
        setPage(DEFAULT_PAGE);
        mutate();
      } else if (message) {
        setBannerMessage(message);
        setShowBanner(true);
        mutate();
      }
    },
    [enqueueSnackbar, mutate, selectedIds, setBannerMessage, setShowBanner]
  );

  useEffect(() => {
    if (userData) {
      const data = userData.data.map((member) => ({
        id: member.id,
        assignee: member.email === assignee?.email,
        email: member.email,
        name: member.name,
        surname: member.surname,
        username: member.username,
        is_integration: member.is_integration,
        enabled: member.enabled,
        group: joinGroups(member.groups_details),
      })) as MemberDataRow[];
      setTableData(data);
      setTotalCount(userData?.totalCount || 0);
    }
  }, [userData, assignee]);

  const initialState = useMemo(() => {
    const entries = tableData.map<[string, boolean]>((v) => [
      v.id.toString(),
      !v.is_integration,
    ]);
    return {
      pageIndex: page,
      pageSize: size,
      sortBy,
      lockedSelectionRowIds: entries ? Object.fromEntries(entries) : {},
      selectedRowIds: selectedIds,
    };
  }, [tableData, page, size, sortBy, selectedIds]);

  const onGlobalFilter = useCallback(
    (
      rows: Row<MemberDataRow>[],
      columnIds: string[],
      filterValue: MembersTableFilter
    ) => globalFilter(rows, columnIds, filterValue, members),
    [globalFilter, members]
  );

  useEffect(() => {
    setPage(DEFAULT_PAGE);
  }, [searchKey]);

  const labels = useMemo(
    () => ({
      selectRowCheckBoxAriaLabel: t("labels.selectIncidentTypeCheckbox"),
    }),
    [t]
  );
  const instance = useHvTable<MemberDataRow>(
    {
      columns,
      data: tableData || [],
      getRowId: useCallback((v: MemberDataRow): string => `${v.id}`, []),
      pageCount: Math.ceil((totalCount ?? 0) / (initialState?.pageSize ?? 1)),
      initialState,
      manualRowSelectedKey: "isSelected",
      labels,
      globalFilter: onGlobalFilter,
      aditivePageBulkSelection: true,
      subtractivePageBulkDeselection: true,
      disableMultiSort: true,
      disableSortRemove: true,
      disableCreateExpandButton: true,
      sortTypes: {
        alphanumeric: (row1, row2, columnName) => {
          const rowOneColumn = row1.values[columnName];
          const rowTwoColumn = row2.values[columnName];
          if (Number.isNaN(Number(rowOneColumn))) {
            const rowOne = rowOneColumn
              ? rowOneColumn.toUpperCase()
              : rowOneColumn;
            const rowTwo = rowTwoColumn
              ? rowTwoColumn.toUpperCase()
              : rowTwoColumn;
            return rowOne > rowTwo ? 1 : -1;
          }
          return Number(rowOneColumn) > Number(rowTwoColumn) ? 1 : -1;
        },
      },
    },

    useHvSortBy,
    useHvPagination,
    useRowSelection,
    useHvBulkActions
  );

  const {
    state: { selectedRowIds },
  } = instance;
  useEffect(() => setSelectedIds(selectedRowIds), [selectedRowIds]);

  return (
    <HvGrid item container spacing={2}>
      <HvGrid item xs={12}>
        <MemberSettingsTableControls />
      </HvGrid>
      <HvGrid item xs={12}>
        <ArchiveDialog
          scope="memberships"
          isOpen={isArchiveDialogOpen}
          promise={archiveUsers}
          onClose={onArchiveClose}
          ids={Object.keys(selectedRowIds)}
        />
        <SimpleTable
          instance={instance}
          bulkActions={bulkActions}
          totalCount={totalCount}
          hideSelectAllPages
        />
      </HvGrid>
    </HvGrid>
  );
};
export default MembersSettingsTable;
